"use client";

import * as React from "react";

import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: string;
  error?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { className, label, error, onCheckedChange, checked, onChange, ...props },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onCheckedChange?.(e.target.checked);
    };

    const checkboxElement = (
      <div className="relative h-4 w-4 shrink-0">
        <input
          type="checkbox"
          className={cn(
            "h-4 w-4 shrink-0 theme-universal:rounded-input theme-universal:border-(length:--ul-theme-border-input-border-weight) theme-universal:border-input-border ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 appearance-none",
            checked
              ? "bg-primary-button theme-universal:border-transparent"
              : "theme-universal:bg-input-bg text-foreground",
            !error && "theme-universal:focus-visible:border-base-focus",
            error && "theme-universal:border-error",
            className
          )}
          ref={ref}
          checked={checked}
          onChange={handleChange}
          data-state={checked ? "checked" : "unchecked"}
          {...props}
        />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Check
            className={cn(
              "h-4 w-4 text-primary-foreground transition-opacity translate-y-[0.1rem]",
              checked ? "opacity-100" : "opacity-0"
            )}
          />
        </div>
      </div>
    );

    if (label) {
      return (
        <div className="flex items-start space-x-2">
          {checkboxElement}
          <label
            htmlFor={props.id}
            className={cn(
              "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
              error && "text-destructive"
            )}
          >
            {label}
          </label>
        </div>
      );
    }

    return checkboxElement;
  }
);
Checkbox.displayName = "Checkbox";

export { Checkbox };
